<template>
  <div class="task-edit">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span class="header-title">{{ isEdit ? $t('autoPush.taskEditTitle') : $t('autoPush.taskNewTitle') }}</span>
        <span class="header-sub">{{ $t('autoPush.taskSub') }}</span>
      </div>

      <el-form ref="taskForm" :model="form" :rules="rules" label-width="110px" class="task-form">
        <!-- 基本信息 -->
        <div class="form-section">
          <div class="section-title"><i class="el-icon-document"></i> {{ $t('autoPush.secBasic') }}</div>
          <el-form-item :label="$t('autoPush.labelName')" prop="name">
            <el-input v-model="form.name" :placeholder="$t('autoPush.phName')" maxlength="50" show-word-limit></el-input>
          </el-form-item>
          <!-- 提醒类型：运营只选"发哪种提醒"，文案与落地页由类型决定（写死在代码里），
               所以后台只有只读预览、没有文案输入 —— 从源头消除"漏填英文"这类事故。
               只在周期推送显示：非周期模式固定为推课程（见 isCourseOnlyMode）。 -->
          <template v-if="form.action === 'reminder' && !isCourseOnlyMode">
            <el-form-item :label="$t('autoPush.labelReminderType')">
              <el-select v-model="form.reminderType" style="width: 100%;">
                <el-option
                  v-for="rt in reminderTypes"
                  :key="rt.key"
                  :label="isEnLocale ? rt.labelEn : rt.label"
                  :value="rt.key"
                ></el-option>
              </el-select>
              <div v-if="currentReminderType" class="form-tip">
                <i class="el-icon-info"></i> {{ isEnLocale ? currentReminderType.descriptionEn : currentReminderType.description }}
              </div>
            </el-form-item>
            <!-- 只读预览：中英用户各自收到什么 + 点开去哪 -->
            <el-form-item v-if="currentReminderType" :label="$t('autoPush.labelReminderPreview')">
              <div style="background: #F7F8FA; border-radius: 6px; padding: 10px 12px; line-height: 1.7;">
                <div style="display: flex; gap: 8px; align-items: baseline;">
                  <span style="color: #909399; font-size: 12px; min-width: 42px;">{{ $t('autoPush.reminderPreviewZh') }}</span>
                  <span style="font-weight: 600;">{{ currentReminderType.sampleTitle }}</span>
                </div>
                <div style="color: #606266; font-size: 13px; padding-left: 50px;">{{ currentReminderType.sampleBody }}</div>
                <div style="display: flex; gap: 8px; align-items: baseline; margin-top: 8px;">
                  <span style="color: #909399; font-size: 12px; min-width: 42px;">English</span>
                  <span style="font-weight: 600;">{{ currentReminderType.sampleTitleEn }}</span>
                </div>
                <div style="color: #606266; font-size: 13px; padding-left: 50px;">{{ currentReminderType.sampleBodyEn }}</div>
                <div class="form-tip">
                  <i class="el-icon-info"></i> {{ $t('autoPush.reminderPreviewTip') }}{{ currentReminderType.deepLink }}
                </div>
              </div>
            </el-form-item>
          </template>
          <!-- 推送模式：周期推送（重复执行）/ 入组周数投放（按入组周数路由）/ 事件触发（资料更新时） -->
          <el-form-item :label="$t('autoPush.labelPushMode')" prop="mode">
            <el-radio-group v-model="form.mode">
              <el-radio-button label="periodic">{{ $t('autoPush.modePeriodic') }}</el-radio-button>
              <el-radio-button label="enrolledWeek">{{ $t('autoPush.modeEnrolledWeek') }}</el-radio-button>
              <el-radio-button label="event">{{ $t('autoPush.modeEvent') }}</el-radio-button>
            </el-radio-group>
            <div class="form-tip">{{ modeTip }}</div>
          </el-form-item>

          <!-- 周期推送模式：按 type + send_time + send_day 排程 -->
          <template v-if="form.mode === 'periodic'">
            <el-form-item :label="$t('autoPush.labelType')" prop="type">
              <el-radio-group v-model="form.type">
                <el-radio-button label="daily">{{ $t('autoPush.typeDailyBtn') }}</el-radio-button>
                <el-radio-button label="weekly">{{ $t('autoPush.typeWeeklyBtn') }}</el-radio-button>
                <el-radio-button label="monthly">{{ $t('autoPush.typeMonthlyBtn') }}</el-radio-button>
              </el-radio-group>
            </el-form-item>
            <el-form-item :label="$t('autoPush.labelSendTime')" prop="sendTime">
              <el-time-picker v-model="form.sendTime" format="HH:mm" value-format="HH:mm" :placeholder="$t('autoPush.phSendTime')"></el-time-picker>
            </el-form-item>
            <el-form-item v-if="form.type === 'weekly'" :label="$t('autoPush.labelSendDay')">
              <el-select v-model="form.sendDay" :placeholder="$t('autoPush.phWeekday')">
                <el-option :label="$t('autoPush.weekday1')" :value="1"></el-option>
                <el-option :label="$t('autoPush.weekday2')" :value="2"></el-option>
                <el-option :label="$t('autoPush.weekday3')" :value="3"></el-option>
                <el-option :label="$t('autoPush.weekday4')" :value="4"></el-option>
                <el-option :label="$t('autoPush.weekday5')" :value="5"></el-option>
                <el-option :label="$t('autoPush.weekday6')" :value="6"></el-option>
                <el-option :label="$t('autoPush.weekday7')" :value="7"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item v-if="form.type === 'monthly'" :label="$t('autoPush.labelSendDay')">
              <el-input-number v-model="form.sendDay" :min="1" :max="28"></el-input-number>
              <span class="form-tip">{{ $t('autoPush.monthDayTip') }}</span>
            </el-form-item>
          </template>

          <!-- 入组周数投放模式：按 enrolled_week + send_day + send_time 路由 -->
          <template v-else-if="form.mode === 'enrolledWeek'">
            <el-form-item :label="$t('autoPush.labelEnrolledWeek')" prop="enrolledWeek">
              <el-input-number v-model="form.enrolledWeek" :min="1" :max="200" controls-position="right" style="width: 200px;"></el-input-number>
              <span class="form-tip">{{ $t('autoPush.enrolledWeekTip') }}</span>
            </el-form-item>
            <el-form-item :label="$t('autoPush.labelSendDay')" prop="sendDay">
              <el-select v-model="form.sendDay" :placeholder="$t('autoPush.phWeekday')">
                <el-option :label="$t('autoPush.weekday1')" :value="1"></el-option>
                <el-option :label="$t('autoPush.weekday2')" :value="2"></el-option>
                <el-option :label="$t('autoPush.weekday3')" :value="3"></el-option>
                <el-option :label="$t('autoPush.weekday4')" :value="4"></el-option>
                <el-option :label="$t('autoPush.weekday5')" :value="5"></el-option>
                <el-option :label="$t('autoPush.weekday6')" :value="6"></el-option>
                <el-option :label="$t('autoPush.weekday7')" :value="7"></el-option>
              </el-select>
              <span class="form-tip">{{ $t('autoPush.enrolledWeekDayTip') }}</span>
            </el-form-item>
            <el-form-item :label="$t('autoPush.labelSendTime')" prop="sendTime">
              <el-time-picker v-model="form.sendTime" format="HH:mm" value-format="HH:mm" :placeholder="$t('autoPush.phSendTime')"></el-time-picker>
            </el-form-item>
          </template>

          <!-- 事件触发模式：无排程配置，用户更新资料命中条件时立即推送 -->
          <template v-else>
            <el-form-item :label="$t('autoPush.labelEventNote')">
              <div class="form-tip event-note">
                <i class="el-icon-info"></i>
                {{ $t('autoPush.eventModeNote') }}
              </div>
            </el-form-item>
          </template>
        </div>

        <!-- 推送内容 -->
        <div class="form-section">
          <div class="section-title"><i class="el-icon-message"></i> {{ $t('autoPush.secContent') }}</div>
          <!-- 推送动作：推课程 / 发提醒通知（非周期模式固定为推课程，控件隐藏） -->
          <el-form-item v-if="form.mode === 'periodic'" :label="$t('autoPush.labelAction')" prop="action">
            <el-radio-group v-model="form.action">
              <el-radio-button label="content">{{ $t('autoPush.actionContent') }}</el-radio-button>
              <el-radio-button label="reminder">{{ $t('autoPush.actionReminder') }}</el-radio-button>
            </el-radio-group>
            <div class="form-tip" v-if="form.action === 'reminder'">{{ $t('autoPush.actionReminderTip') }}</div>
          </el-form-item>

          <!-- 推课程：必须选具体课程 -->
          <el-form-item v-if="form.action === 'content'" :label="$t('autoPush.labelContent')" prop="contentId">
            <el-select v-model="form.contentId" :placeholder="$t('autoPush.phContent')" filterable clearable :style="{ width: '100%' }" :popper-class="'wc-content-popper'">
              <el-option v-for="c in contentOptions" :key="c.id" :label="c.title" :value="c.id">
                <span class="opt-title">{{ c.title }}</span>
                <el-tag v-if="c.isBilingual" size="mini" type="info" class="opt-lang">双语</el-tag>
                <el-tag v-else size="mini" :type="c.languages[0] === 'en' ? 'warning' : 'success'" class="opt-lang">
                  {{ c.languages[0] === 'en' ? 'English' : '中文' }}
                </el-tag>
              </el-option>
            </el-select>
          </el-form-item>

          <!-- 提醒通知：不推内容，按用户开关自动发邮件 / iOS 推送 -->
          <el-form-item v-else :label="$t('autoPush.labelContent')">
            <span class="form-tip">{{ $t('autoPush.reminderNoContentTip') }}</span>
          </el-form-item>

          <!-- 提醒冷却：像「连续未读推送数」这类条件一旦命中就持续为真（用户不读就一直满足），
               没有冷却期会每天扫描都重发一次；0 = 不限制（保持每天可提醒） -->
          <el-form-item v-if="form.action === 'reminder'" :label="$t('autoPush.labelCooldown')">
            <el-input-number v-model="form.reminderCooldownDays" :min="0" :max="365" controls-position="right" style="width: 160px;"></el-input-number>
            <span class="form-tip">{{ $t('autoPush.cooldownTip') }}</span>
          </el-form-item>
        </div>

        <!-- 目标范围 -->
        <div class="form-section">
          <div class="section-title"><i class="el-icon-user"></i> {{ $t('autoPush.secTarget') }}</div>
          <el-form-item :label="$t('autoPush.labelTargetType')">
            <el-checkbox-group v-model="targetTypes" @change="handleTargetTypeChange">
              <el-checkbox label="all">{{ $t('autoPush.tgAll') }}</el-checkbox>
              <el-checkbox label="batch">{{ $t('autoPush.tgBatch') }}</el-checkbox>
              <el-checkbox label="group">{{ $t('autoPush.tgGroup') }}</el-checkbox>
              <el-checkbox label="condition">{{ $t('autoPush.tgCondition') }}</el-checkbox>
            </el-checkbox-group>
            <div class="form-tip" v-if="multiDimTarget">
              <i class="el-icon-info"></i> {{ $t('autoPush.unionTip') }}
            </div>
          </el-form-item>
          <el-form-item v-if="targetTypes.includes('batch')" :label="$t('autoPush.labelTargetBatch')" prop="targetBatchId">
            <el-select v-model="form.targetBatchId" :placeholder="$t('autoPush.phTargetBatch')" filterable clearable>
              <el-option v-for="b in batchOptions" :key="b.id" :label="b.name" :value="b.id"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item v-if="targetTypes.includes('group')" :label="$t('autoPush.labelTargetGroup')" prop="targetGroupIds">
            <!-- 多分组：一个任务可投放给多个分组（并集）；tag 全量展示，样式与用户详情一致 -->
            <el-select v-model="form.targetGroupIds" :placeholder="$t('autoPush.phTargetGroup')" filterable clearable multiple style="width: 100%;">
              <el-option v-for="g in groupOptions" :key="g.id" :label="g.name" :value="g.id"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item v-if="targetTypes.includes('condition')" :label="$t('autoPush.labelCondition')" prop="conditionId">
            <!-- 条件选择 + 就地新建/编辑（2026-10-03）：原先运营必须先去「条件管理」建好
                 再回来重选，来回跳转且容易丢失已填的任务表单。现在直接在本页开对话框。
                 下拉项带规则摘要，便于一眼确认选的是哪个条件。 -->
            <div class="condition-picker">
              <el-select v-model="form.conditionId" :placeholder="$t('autoPush.phCondition')" clearable filterable class="condition-select" popper-class="wc-condition-popper">
                <el-option v-for="c in filteredConditionOptions" :key="c.id" :label="c.name" :value="c.id">
                  <span class="cond-option-name">{{ c.name }}</span>
                  <span class="cond-option-summary">{{ conditionSummary(c) }}</span>
                </el-option>
              </el-select>
              <el-button type="text" icon="el-icon-plus" @click="openConditionDialog()">{{ $t('autoPush.condQuickNew') }}</el-button>
              <el-button v-if="form.conditionId" type="text" @click="openConditionDialog(form.conditionId)">{{ $t('autoPush.condQuickEdit') }}</el-button>
            </div>
            <!-- 事件触发模式只列含资料字段的条件，并说明原因 -->
            <span class="form-tip" v-if="form.mode === 'event'">{{ $t('autoPush.conditionEventTip') }}</span>
            <span class="form-tip" v-else>{{ $t('autoPush.conditionTip') }}</span>
          </el-form-item>
        </div>

        <!-- 状态 -->
        <div class="form-section">
          <div class="section-title"><i class="el-icon-setting"></i> {{ $t('autoPush.secStatus') }}</div>
          <el-form-item :label="$t('autoPush.labelStatus')">
            <el-switch v-model="form.status" active-value="active" inactive-value="inactive" :active-text="$t('autoPush.switchActive')" :inactive-text="$t('autoPush.switchInactive')"></el-switch>
          </el-form-item>
        </div>

        <el-form-item class="form-actions">
          <el-button type="primary" :loading="submitLoading" @click="handleSubmit">{{ $t('autoPush.save') }}</el-button>
          <el-button @click="$router.back()">{{ $t('autoPush.cancel') }}</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 条件对话框：新建/编辑条件后自动选中，不用离开当前任务表单 -->
    <condition-form-dialog
      :visible.sync="conditionDialogVisible"
      :condition-id="editingConditionId"
      @saved="onConditionSaved"
    />
  </div>
</template>

<script>
import { getTask, saveTask, updateTask, getConditions, getReminderTypes } from '@/api/autoPush'
import { getContents } from '@/api/pushes'
import { getList as getBatchList } from '@/api/userBatch'
import { getList as getGroupList } from '@/api/userGroup'
import ConditionFormDialog from './ConditionFormDialog.vue'
// 条件下拉的规则摘要（如「本周录音数 = 0」），与条件表单共用一份格式化逻辑
import { summarizeCondition } from './condition-form'

export default {
  name: 'AutoPushTaskEdit',
  components: { ConditionFormDialog },
  data() {
    return {
      isEdit: false,
      form: {
        name: '',
        // 提醒类型（仅提醒通知用）：文案与落地页由类型决定，见后端 Reminders 注册表
        reminderType: 'unread_course',
        mode: 'periodic',
        action: 'content',
        type: 'daily',
        sendTime: '09:00',
        sendDay: 1,
        enrolledWeek: null,
        // 提醒冷却天数：仅「提醒通知」动作有意义；null/0 = 不限制
        reminderCooldownDays: null,
        contentId: '',
        targetType: 'all',
        targetBatchId: '',
        // 多分组：数组，投放给所选分组的并集
        targetGroupIds: [],
        conditionId: '',
        status: 'active'
      },
      targetTypes: ['all'],
      rules: {
        name: [{ required: true, message: this.$t('autoPush.ruleName'), trigger: 'blur' }],
        mode: [{ required: true, message: this.$t('autoPush.ruleMode'), trigger: 'change' }],
        type: [{ required: true, message: this.$t('autoPush.ruleType'), trigger: 'change' }],
        sendTime: [{ required: true, message: this.$t('autoPush.ruleSendTime'), trigger: 'change' }],
        contentId: [{ required: true, message: this.$t('autoPush.ruleContent'), trigger: 'change' }],
        // 仅作用于入组周数模式的发送日（周期模式的下拉未挂 prop，不受影响）
        sendDay: [{
          validator: (rule, value, callback) => {
            if (value === null || value === undefined || value === '' || value < 1 || value > 7) {
              callback(new Error(this.$t('autoPush.ruleSendDay')))
            } else {
              callback()
            }
          },
          trigger: 'change'
        }]
      },
      contentOptions: [],
      batchOptions: [],
      groupOptions: [],
      conditionOptions: [],
      // 条件对话框（新建/编辑后自动选中）
      conditionDialogVisible: false,
      editingConditionId: null,
      // 提醒类型清单（来自后端注册表，只含已实现的类型）
      reminderTypes: [],
      submitLoading: false
    }
  },
  computed: {
    // 是否同时选择了多个目标维度（批次/分组/条件 ≥2），多维度时后端取交集（AND）
    multiDimTarget() {
      const dims = [
        this.targetTypes.includes('batch'),
        this.targetTypes.includes('group'),
        this.targetTypes.includes('condition')
      ].filter(Boolean).length
      return dims >= 2
    },
    // 推送模式说明文案
    modeTip() {
      if (this.form.mode === 'enrolledWeek') return this.$t('autoPush.modeEnrolledWeekTip')
      if (this.form.mode === 'event') return this.$t('autoPush.modeEventTip')
      return this.$t('autoPush.modePeriodicTip')
    },
    // 事件触发模式下只列出「含资料字段」的条件：
    // 浏览/未读等行为类字段不会因资料更新而变化，配成事件触发永远不会命中
    // 当前选中的提醒类型（下拉与只读预览共用一份）
    currentReminderType() {
      return (this.reminderTypes || []).find(rt => rt.key === this.form.reminderType) || null
    },
    // 后台界面语言：用于选择类型名称/说明的中英版本
    isEnLocale() {
      // 本项目没有 vue-i18n，只挂了 $t / $getLang()（见 src/i18n/index.js）。
      // 曾写成 this.$i18n && this.$i18n.locale —— $i18n 恒为 undefined，于是英文界面下
      // 提醒类型的名称与说明永远退回中文。
      return this.$getLang() === 'en'
    },
    filteredConditionOptions() {
      if (this.form.mode !== 'event') return this.conditionOptions
      return this.conditionOptions.filter(c => c.isProfileSensitive)
    },
    // 是否为事件触发模式
    isEventMode() {
      return this.form.mode === 'event'
    },
    // 是否为「非周期」模式（入组周数投放 / 事件触发）
    //
    // 这两种模式一律固定为「推课程内容」：它们只决定"什么时候推"（按入组周数路由 /
    // 资料更新时触发），而提醒通知（文案由 reminder_type 决定、不推课程）只在周期推送
    // 里有意义。换言之，非周期模式下 action 不是运营可选项，故隐藏其控件。
    isCourseOnlyMode() {
      return this.form.mode !== 'periodic'
    }
  },
  watch: {
    // 非周期模式（入组周数投放 / 事件触发）固定为「推课程内容」：
    // 这两种模式没有 action 单选，若用户从周期推送的「发提醒通知」切过来，
    // form.action 仍是 reminder，会同时踩三个坑：
    //   ① 显示层按 action 渲染 → 界面显示提醒类型、提醒文案预览与"不推具体课程"
    //      提示，运营以为自己配的是提醒；
    //   ② 提交时 payload 又按模式归位成 content（见 handleSubmit）→ 保存结果与界面
    //      不符，且课程下拉不显示、contentId 的必填校验落不到实处；
    //   ③ 若是旧前端缓存/脚本把 reminder 真的提交进后端，任务会按提醒发
    //      （AutoPushService 的提醒分支不看 mode），课程永远不发 = 静默漏课。
    // 所以在「切换模式」这一刻就归位，让界面、提交值与落库值三者一致。
    'form.mode'(newMode) {
      if (newMode !== 'periodic') {
        this.form.action = 'content'
      }
    }
  },
  created() {
    const id = this.$route.params.id
    if (id) {
      this.isEdit = true
      this.loadDetail(id)
    }
    this.loadOptions()
  },
  methods: {
    loadDetail(id) {
      getTask(id).then(res => {
        const data = res.data || {}
        this.form = {
          id: data.id,
          name: data.name || '',
          reminderType: data.reminderType || 'unread_course',
          // 优先按 triggerMode 判定事件触发；其次有 enrolledWeek 为入组周数投放；否则周期推送
          mode: data.triggerMode === 'event'
            ? 'event'
            : (data.enrolledWeek ? 'enrolledWeek' : 'periodic'),
          action: data.action || 'content',
          type: data.type || 'daily',
          sendTime: data.sendTime || '09:00',
          // 不能写成 data.sendDay || 1：会把合法的 0 与 monthly 的 1-28 一并吞掉
          sendDay: (data.sendDay !== null && data.sendDay !== undefined) ? data.sendDay : 1,
          enrolledWeek: data.enrolledWeek || null,
          reminderCooldownDays: data.reminderCooldownDays || null,
          contentId: data.pushContentId || data.contentId || '',
          targetType: data.targetType || 'all',
          targetBatchId: data.targetBatchId || '',
          // 多分组：回填任务已配置的分组数组
          targetGroupIds: data.targetGroupIds || [],
          conditionId: data.conditionId || '',
          status: (data.status === 1 || data.status === 'active') ? 'active' : 'inactive'
        }
        // 历史脏数据归位：非周期模式曾被允许保存成 action=reminder（旧版表单从周期模式
        // 切过来时不归位），这类任务会发提醒而不是课程。打开时按固定语义显示为推课程，
        // 运营保存一次即同步修正库里的值（后端更新时也会强制归位，双保险）。
        if (this.form.mode !== 'periodic') {
          this.form.action = 'content'
        }
        // 根据 targetType 还原多选状态
        this.targetTypes = this.parseTargetTypes(this.form.targetType)
        // 若内容选项已先加载完成，此处归一化编辑回显的 contentId
        if (this.contentOptions.length) {
          this.form.contentId = this.normalizeContentId(this.form.contentId)
        }
      }).catch(() => {
        this.$message.error(this.$t('autoPush.loadDetailFail'))
      })
    },
    parseTargetTypes(targetType) {
      // 优先根据已回显的具体 id 推断勾选状态（combined 可能是批次/分组/条件的任意组合）
      const types = []
      if (this.form.targetBatchId) types.push('batch')
      if ((this.form.targetGroupIds || []).length) types.push('group')
      if (this.form.conditionId) types.push('condition')
      if (types.length) return types
      if (!targetType || targetType === 'all') return ['all']
      if (targetType === 'batch') return ['batch']
      if (targetType === 'group') return ['group']
      if (targetType === 'condition') return ['condition']
      return ['all']
    },
    loadOptions() {
      // 与手动推送弹窗统一：使用推送专用接口（只返回启用课程，不分页）
      getContents().then(res => {
        const list = res.data.contents || res.data || []
        this.contentOptions = this.buildContentGroups(list)
        // 详情加载早于选项时，此处兜底归一化编辑回显的 contentId
        this.form.contentId = this.normalizeContentId(this.form.contentId)
      }).catch(() => {})
      getBatchList().then(res => {
        this.batchOptions = res.data || []
      })
      getGroupList().then(res => {
        this.groupOptions = res.data || []
      })
      this.loadConditionOptions()
      // 提醒类型清单（只含后端已实现的类型；新增类型时前端无需改动）
      getReminderTypes().then(res => {
        this.reminderTypes = res.data || []
      }).catch(() => {})
    },
    // 拉取条件列表（新建/编辑条件后也要重新拉：拿最新的名称与「是否含资料字段」标记）
    loadConditionOptions() {
      return getConditions().then(res => {
        this.conditionOptions = res.data || []
        return this.conditionOptions
      }).catch(() => [])
    },
    // 打开条件对话框：id 为空 = 新建，有值 = 编辑该条件
    openConditionDialog(id) {
      this.editingConditionId = id || null
      this.conditionDialogVisible = true
    },
    /**
     * 条件保存成功：自动选中它，并处理两种边界
     *   1) 新条件若被设为「停用」，不会出现在下拉里（列表接口只返回启用条件）→ 提示并清空选中，
     *      否则下拉会显示成一个裸 id；
     *   2) 事件触发模式只接受「含资料字段」的条件 → 若新条件不含，立刻提示，避免保存任务时
     *      被后端拒绝却不知道为什么（后端同样有校验）。
     */
    onConditionSaved(saved) {
      this.conditionDialogVisible = false
      if (!saved || !saved.id) return
      this.loadConditionOptions().then(list => {
        const hit = (list || []).find(c => c.id === saved.id)
        if (!hit) {
          this.form.conditionId = ''
          this.$message.warning(this.$t('autoPush.condInactiveTip'))
          return
        }
        this.form.conditionId = saved.id
        if (this.form.mode === 'event' && !hit.isProfileSensitive) {
          this.$message.warning(this.$t('autoPush.condEventNeedProfile'))
        }
      })
    },
    // 下拉项里的规则摘要（如「本周录音数 = 0」）
    conditionSummary(condition) {
      return summarizeCondition(condition, key => this.$t('autoPush.' + key))
    },
    // 按语言组聚合：同一 languageGroupId 的中英两版合并为一项，存组代表 contentId
    // 双语课程标题拼接为「中文标题 / English Title」（中文在前），与手动推送弹窗一致
    buildContentGroups(list) {
      const map = {}
      list.forEach(c => {
        const gid = c.languageGroupId || c.id
        if (!map[gid]) {
          map[gid] = { id: c.id, languageGroupId: gid, languages: [], titles: [], ids: [] }
        }
        if (map[gid].languages.indexOf(c.language) === -1) {
          map[gid].languages.push(c.language)
        }
        // 同一语言只保留一条标题（防数据异常导致标题重复）
        if (map[gid].titles.every(t => t.language !== c.language)) {
          map[gid].titles.push({ language: c.language, title: c.title })
        }
        map[gid].ids.push(c.id)
      })
      return Object.keys(map).map(gid => {
        const g = map[gid]
        const isBilingual = g.languages.length > 1
        // 双语课程把中英标题都展示出来（中文在前，英文在后）
        const title = isBilingual
          ? g.titles.slice().sort((a, b) => {
              if (a.language === 'zh') return -1
              if (b.language === 'zh') return 1
              return 0
            }).map(t => t.title).join(' / ')
          : (g.titles[0] && g.titles[0].title)
        return {
          id: g.id,
          title,
          languageGroupId: g.languageGroupId,
          languages: g.languages,
          ids: g.ids,
          isBilingual
        }
      })
    },
    // 编辑回显：任务里存的 contentId 可能是双语组内非代表版本，映射到组代表 id 以便下拉框正确匹配
    normalizeContentId(contentId) {
      if (!contentId) return contentId
      if (this.contentOptions.some(c => c.id === contentId)) return contentId
      const group = this.contentOptions.find(c => (c.ids || []).includes(contentId))
      return group ? group.id : contentId
    },
    handleTargetTypeChange(val) {
      // 互斥逻辑：选 all 时清空 batch/group/condition；选具体维度时移除 all
      if (val.includes('all') && val.length > 1) {
        const last = val[val.length - 1]
        if (last === 'all') {
          this.targetTypes = ['all']
          this.form.targetBatchId = ''
          this.form.targetGroupIds = []
          this.form.conditionId = ''
        } else {
          this.targetTypes = val.filter(v => v !== 'all')
        }
        return
      }
      // 取消勾选时联动清空对应下拉，避免"未勾选但下拉残留旧值"
      if (!val.includes('batch')) {
        this.form.targetBatchId = ''
      }
      if (!val.includes('group')) {
        this.form.targetGroupIds = []
      }
      if (!val.includes('condition')) {
        this.form.conditionId = ''
      }
    },
    // 计算目标类型：单维度（批次/分组/条件）各自单独生效；多个维度时取交集（combined）
    buildTargetType() {
      const types = this.targetTypes
      // "全部用户"优先级最高：勾选后忽略批次/分组/条件
      if (types.includes('all')) return 'all'
      const hasBatch = types.includes('batch')
      const hasGroup = types.includes('group')
      const hasCondition = types.includes('condition')
      const dims = [hasBatch, hasGroup, hasCondition].filter(Boolean).length
      if (dims === 0) return 'all'
      if (dims === 1) {
        if (hasCondition) return 'condition'
        if (hasBatch) return 'batch'
        return 'group'
      }
      return 'combined'
    },
    handleSubmit() {
      this.$refs.taskForm.validate(valid => {
        if (!valid) return
        // 校验：必须至少勾选一个目标维度（不允许提交空的 targetTypes）
        if (!this.targetTypes || this.targetTypes.length === 0) {
          this.$message.warning(this.$t('autoPush.warnTargetEmpty'))
          return
        }
        // 校验：选了批次必须选具体批次
        if (this.targetTypes.includes('batch') && !this.form.targetBatchId) {
          this.$message.warning(this.$t('autoPush.warnBatch'))
          return
        }
        if (this.targetTypes.includes('group') && !(this.form.targetGroupIds || []).length) {
          this.$message.warning(this.$t('autoPush.warnGroup'))
          return
        }
        if (this.targetTypes.includes('condition') && !this.form.conditionId) {
          this.$message.warning(this.$t('autoPush.warnCondition'))
          return
        }
        // 入组周数投放模式：必须填入组周数
        if (this.form.mode === 'enrolledWeek' && !this.form.enrolledWeek) {
          this.$message.warning(this.$t('autoPush.warnEnrolledWeek'))
          return
        }
        // 事件触发模式：必须勾选「条件」维度并选定具体条件（否则不知道推给谁）
        if (this.form.mode === 'event') {
          if (!this.targetTypes.includes('condition')) {
            this.$message.warning(this.$t('autoPush.warnEventNeedCondition'))
            return
          }
          if (!this.form.conditionId) {
            this.$message.warning(this.$t('autoPush.warnCondition'))
            return
          }
        }
        // 推课程动作：必须选具体课程
        if (this.form.action === 'content' && !this.form.contentId) {
          this.$message.warning(this.$t('autoPush.warnContent'))
          return
        }
        this.submitLoading = true
        const payload = {
          ...this.form,
          targetType: this.buildTargetType(),
          // 触发方式：event=事件触发（资料更新时命中条件即推），其余为 scheduled（定时扫描）
          triggerMode: this.form.mode === 'event' ? 'event' : 'scheduled',
          // 周期推送模式：入组周数置空（不限）
          enrolledWeek: this.form.mode === 'enrolledWeek' ? (this.form.enrolledWeek || null) : null,
          // 入组周数投放 / 事件触发模式：排程不生效，type 统一按 daily 存储
          type: this.form.mode === 'periodic' ? this.form.type : 'daily',
          // 入组周数投放与事件触发只推课程；提醒通知不推内容（contentId 清空避免后端误读）
          action: this.form.mode === 'periodic' ? this.form.action : 'content',
          contentId: this.form.action === 'reminder' ? '' : this.form.contentId,
          // 提醒冷却天数：仅「周期推送 + 提醒通知」有意义；其余情况一律置空，
          // 避免先配了冷却再切换成推课程时留下无意义残值
          reminderCooldownDays: (this.form.mode === 'periodic' && this.form.action === 'reminder')
            ? (this.form.reminderCooldownDays || null)
            : null,
          // 事件触发无排程概念：清空发送日与发送时间
          sendDay: this.form.mode === 'event' ? 0 : this.form.sendDay,
          sendTime: this.form.mode === 'event' ? null : this.form.sendTime
        }
        const api = this.isEdit ? updateTask(this.form.id, payload) : saveTask(payload)
        api.then(() => {
          this.$message.success(this.isEdit ? this.$t('autoPush.updateSuccess') : this.$t('autoPush.createSuccess'))
          this.$router.back()
        }).catch(() => {
          this.$message.error(this.$t('autoPush.saveFail'))
        }).finally(() => {
          this.submitLoading = false
        })
      })
    }
  }
}
</script>

<style scoped>
.task-edit {
  max-width: 820px;
  margin: 0 auto;
}
.opt-title {
  display: inline-block;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.opt-lang {
  margin-left: 6px;
  vertical-align: middle;
}
.card-header {
  display: flex;
  flex-direction: column;
}
.header-title {
  font-weight: bold;
  font-size: 18px;
  color: #303133;
}
.header-sub {
  font-size: 13px;
  color: #909399;
  margin-top: 4px;
}
.task-form {
  padding: 10px 20px;
}
.form-section {
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 1px dashed #ebeef5;
}
.form-section:last-of-type {
  border-bottom: none;
}
.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 18px;
  padding-left: 8px;
  border-left: 3px solid #409eff;
}
.section-title i {
  color: #409eff;
  margin-right: 6px;
}
.form-tip {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}
.form-actions {
  margin-top: 10px;
  text-align: center;
}
/* 条件选择行：下拉 + 「新建条件 / 编辑」两个文字按钮（2026-10-03） */
.condition-picker {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.condition-select {
  flex: 0 0 320px;
}
.form-actions >>> .el-form-item__content {
  margin-left: 0 !important;
}
</style>

<style>
/* 全局：推送内容下拉框最小宽度，确保长标题完整展示（不受 scoped 影响） */
.wc-content-popper {
  min-width: 580px !important;
}
/* 触发条件下拉项：条件名左、规则摘要右（下拉渲染在 body 下，scoped 样式不生效，必须放全局） */
.wc-condition-popper .cond-option-name {
  float: left;
}
.wc-condition-popper .cond-option-summary {
  float: right;
  margin-left: 16px;
  color: #909399;
  font-size: 12px;
}
.wc-condition-popper .el-select-dropdown__item {
  /* 摘要较宽，给下拉框一点余量，避免名字被挤断 */
  min-width: 340px;
}
</style>
